/// <amd-module name="VC/Common/RepositoryPickerProvider" />
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";

import { IArtifactPickerProviderOptions, ArtifactPickerProviderBase, IFavoritesContext } from "Favorites/Picker/ArtifactPickerProvider";
import { Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService, IFavoritesCollection } from "Favorites/Common/FavoritesService";

import { IPickListAction } from "VSSUI/Components/PickList";
import { IVssIconProps, VssIconType } from "VSSUI/VssIcon";

import { GitRepository } from "./Contracts/Git";
import { startTelemetryTimer } from "./Utils/Telemetry";
import { IDeprecatedGitRepositoryService } from "./GitRepositoryService";
import * as Resources from "VC/Common/Resources";

/**
 * An item in a repository picker
 */
export interface IRepositoryPickerItem {

    /**
     * Display name of the repository ($/ProjectName for TFVC)
     */
    name: string;

    /**
     * If true, this represents the single TFVC repository
     */
    isTfvc: boolean;

    /**
     * If true, this git repository is a fork
     */
    isFork: boolean;

    /**
     * Repository id
     */
    id: string;

    /**
     * Git repository information (if a git repository)
     */
    repository?: GitRepository;
}

export interface IRepositoryPickerOptions {
    
    /**
     * The name of the project under which to show repositories
     */
    projectName?: string;

    /**
     * The id of the project under which to show repositories
     */
    projectId?: string;

    /**
     * Optional delegate to get the href property of the pick list item
     */
    getItemHref?: (repositoryItem: IRepositoryPickerItem) => string;

    /**
     * Optional list of repositories. If not provided, service IGitRepositoryService.getAllRepositories will be used.
     */
    getRepositories?: () => Promise<GitRepository[]>;

    /**
     * Optional list of actions to render after the repositories in the list
     */
    actions?: IPickListAction[];
    
    /**
     * Callback invoked whenever a new repository is selected
     */
    onRepositorySelected?: (selectedRepository: IRepositoryPickerItem) => void | boolean;

    /**
     * Is the selected repository the TFVC repository
     */
    selectedRepositoryIsTfvc?: boolean;

    /**
     * The name of the selected repository
     * */
    selectedRepositoryName?: string;

    /**
     * The id of the selected repository
     */
    selectedRepositoryId?: string;
    
    /**
     * Is the selected repository a fork of another repository (Git only)
     */
    selectedRepositoryIsFork?: boolean;
    
    /**
     * Does the current project support TFVC?
     */
    supportsTfvc?: boolean;

    /**
     * Header text for the default group
     */
    defaultGroupHeader?: string;

    /**
     * Header text for the favorites group
     */
    favoriteGroupHeader?: string;

    /**
     * Show favorites as soon as they are available, instead of waiting for repositories to be loaded. True by default.
     * This may cause that favorites displayed are not present on the items, so if your repositories list is filtered out,
     * you may want to disable this option to prevent leaks.
     */
    showFavoritesEarly?: boolean;
}

const RepositoryFavoriteArtifactType = "Microsoft.TeamFoundation.Git.Repository";
const telemetryFeatureName = "RepositoryPickerProvider";

export class RepositoryPickerProvider extends ArtifactPickerProviderBase<IRepositoryPickerItem> {

    private itemsPromise: Promise<IRepositoryPickerItem[]>;
    private pageContext: IVssPageContext;
    private projectId: string | undefined;
    private projectName: string | undefined;
    private supportsTfvc: boolean | undefined;
    private artifactPickerOptions: IArtifactPickerProviderOptions<IRepositoryPickerItem>;

    /**
     * Display search box always.
     */
    public minItemsForSearchBox = 0;

    constructor(pageContext: IVssPageContext, options: IRepositoryPickerOptions) {
        super();

        const { 
            actions,
            getItemHref,
            getRepositories,
            onRepositorySelected,
            selectedRepositoryIsTfvc,
            selectedRepositoryIsFork,
            selectedRepositoryId,
            selectedRepositoryName,
            supportsTfvc,
            projectName,
            projectId,
            defaultGroupHeader,
            favoriteGroupHeader,
            showFavoritesEarly = true,
        } = options;
        
        this.pageContext = pageContext;
        this.projectId = projectId;
        this.projectName = projectName;
        this.supportsTfvc = supportsTfvc;

        if (getRepositories) {
            this.itemsPromise = getRepositories().then(repositories => repositories.map(createRepositoryPickerItem));
        }   

        let selectedItem: IRepositoryPickerItem | undefined;
        if (selectedRepositoryIsTfvc) {
            selectedItem = { name: "$/" + projectName, isTfvc: true, id: "tfvc", isFork: false };
        }
        else if (selectedRepositoryName) {
            selectedItem = { name: selectedRepositoryName, isTfvc: false, id: selectedRepositoryId || selectedRepositoryName, isFork: !!selectedRepositoryIsFork };
        }

        let favoritesContext: IFavoritesContext;
        if (projectName && projectId) {
            favoritesContext = {
                artifactType: RepositoryFavoriteArtifactType,
                artifactScope: {
                    id: projectId,
                    name: projectName,
                    type: "Project"
                }
            };
        }
        else {
            favoritesContext = {
                artifactType: RepositoryFavoriteArtifactType,
                artifactScope: {
                    id: "",
                    name: "",
                    type: "Collection"
                }
            };
        }
    
        this.artifactPickerOptions = {
            pageContext,
            favoritesContext,
            onArtifactClicked: onRepositorySelected,
            selectedArtifact: selectedItem,
            defaultGroupHeader,
            favoriteGroupHeader,
            hideFavoriteSelectedItemIndicator: true,
            getArtifacts: this.getPickListItems,
            getArtifactId: repo => repo.id,
            getArtifactName: repo => repo.name,
            getArtifactHref: getItemHref,
            getArtifactIcon: (repo: IRepositoryPickerItem): IVssIconProps => {
                return {
                    iconType: VssIconType.bowtie,
                    iconName: repo.isTfvc ? "tfvc-repo" : repo.isFork ? "git-fork" : "git"
                }
            },
            getArtifactFromFavorite: showFavoritesEarly
                ? favorite => {
                    return {
                        name: favorite.artifactName,
                        id: favorite.artifactId,
                        isTfvc: false,
                        isFork: false,
                    };
                }
                : undefined,
            getFavoriteFromArtifact: repo => {
                if (repo.repository) {
                    return {
                        artifactId: repo.repository.id,
                        artifactName: repo.repository.name,
                        artifactScope: {
                            id: repo.repository.project.id,
                            name: repo.repository.project.name,
                            type: "Project"
                        },
                        artifactType: RepositoryFavoriteArtifactType
                    } as Favorite;
                }
                else {
                    return undefined;
                }
            },
            artifactComparer: (repo1: IRepositoryPickerItem, repo2: IRepositoryPickerItem): number => {
                return repo1.name.localeCompare(repo2.name, navigator.language);
            },
            otherActions: actions,
            searchTextPlaceholder: Resources.SearchRepositoriesWatermark,
            ariaLabelFormat: Resources.RepositoryDropdown_AriaLabel,
        };

        this.initialize(this.artifactPickerOptions);
    }

    private getPickListItems = (): Promise<IRepositoryPickerItem[]> => {
        if (!this.itemsPromise) {
            const telemetryTimer = startTelemetryTimer(this.pageContext, telemetryFeatureName);

            const repositoryService = this.pageContext.getService<IDeprecatedGitRepositoryService>("IDeprecatedGitRepositoryService");
            this.itemsPromise = repositoryService.getAllRepositories(this.projectId).then((repositories) => {

                const repoListItems = repositories.map(createRepositoryPickerItem);

                // Sort the git repos alphabetically by name
                repoListItems.sort((repo1, repo2) => {
                    return localeIgnoreCaseComparer(repo1.name, repo2.name);
                });

                // Add a TFVC entry to the front of the list if the project supports it
                if (this.supportsTfvc) {
                    repoListItems.unshift({
                        name: "$/" + this.projectName,
                        isTfvc: true,
                        id: "tfvc",
                        isFork: false,
                    });
                }

                telemetryTimer.publishTime("getAllRepositories");

                return repoListItems;
            });
        }
        return this.itemsPromise;
    }

    protected async _loadFavorites(includeExtendedData: boolean = false): Promise<IFavoritesCollection | undefined> {
        const telemetryTimer = startTelemetryTimer(this.pageContext, telemetryFeatureName);
        const favorites = await super._loadFavorites(includeExtendedData);

        if (favorites) {
            telemetryTimer.publishTime(
                "loadFavorites",
                {
                    includeExtendedData
                });
        }

        return favorites;
    }

    /**
     * Override base method to not execute FPS navigation when not in verticals
     */
    public onSelectedItemChanged = (artifact: IRepositoryPickerItem): void | boolean => {
        this.setSelectedItem(artifact);

        const { onArtifactClicked, getArtifactHref } = this.artifactPickerOptions;
        if (onArtifactClicked) {
            return onArtifactClicked(artifact);
        }

        if (getArtifactHref) {
            return onClickFPS(this.pageContext, getArtifactHref(artifact), true, undefined, { telemetrySource: telemetryFeatureName });
        }
    }
}

function createRepositoryPickerItem(repo: GitRepository): IRepositoryPickerItem {
    return {
        name: repo.name,
        id: repo.id,
        repository: repo,
        isFork: repo.isFork,
        isTfvc: false,
    };
}